import { useState, useEffect } from 'preact/hooks';
import { useScreenSize, SCREEN_SIZES } from 'react-hooks/useScreenSize';
import { Button, BUTTON_SIZES } from './Button';
import ProductQuantity from './ProductQty';
import { XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { Image } from '@mantine/core';
import { Carousel } from '@mantine/carousel';

export const StarterKitModalComponent = ({ isOpen, onClose, product }) => {
    const { screenSize } = useScreenSize();
    const isMobile = screenSize === SCREEN_SIZES.MOBILE;
  
    const images = product.product_images || [];
    
    const [selectedImage, setSelectedImage] = useState(images[0]?.src || '');
    const [isProductInCart, setIsProductInCart] = useState(false);

    useEffect(() => {
        if (window.cirkulServices?.cart?.cartItems) {
            setIsProductInCart(
                Object.values(window.cirkulServices.cart.cartItems).some(
                    (item) => item.sku === product.sku
                )
            );
        }
    }, [isOpen, product.sku]);    

    const handleAddToCart = () => {
        const item = {
            id: product.id,
            quantity: 1
        };

        if (window.cirkulServices?.cart?.cartItems) {
          window.cirkulServices.cart.addToCart(item).then(() => {
            setIsProductInCart(true);
          });
        }
    };
    
    const handleDownloadAndOpenPDF = () => {
        const pdfUrl = product.nutrition_facts_pdf;
        window.open(pdfUrl, "_blank");
    };

    if (!isOpen) return null; // Move this after the useEffect

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
      <div className={`tw-relative tw-w-full tw-rounded-[5px] tw-bg-white tw-shadow-lg 
                        ${isMobile ? 'tw-max-h-screen tw-max-w-[358px] tw-overflow-y-auto tw-overflow-x-hidden' : 'tw-max-w-[850px]'}`}>
        <div className={`tw-flex ${isMobile ? 'tw-flex-col' : ''}`}>
          <div className='tw-flex tw-flex-col tablet:tw-w-1/2 tablet:tw-justify-end'>
            {/* Product Images - Mantine Carousel for Mobile */}
            {isMobile ? (
              <Carousel
              slideSize="100%"
              height={200}
              slideGap="xl"
              controlsOffset="xs"
              loop
              withControls
              withIndicators={images.length > 1}
              classNames={{
                indicators: 'tw-mt-16 tw-flex tw-justify-center tw-gap-[4px] tw-w-full',
                indicator:
                  'tw-w-[8px] tw-h-[8px] tw-bg-[#AFB6B4] tw-rounded-full tw-mx-[2px] data-[active=true]:tw-bg-[#00A3E0] data-[active=true]:tw-w-[16px]',
                container: 'tw-relative tw-flex',
                slide: 'tw-flex-none tw-w-full tw-relative',
                control: 'tw-w-32 tw-h-32 tw-text-blue-300',
                controls: 'tw-flex tw-absolute tw-justify-between tw-w-full tw-top-[30%]',
                viewport: 'tw-flex tw-flex-col tw-justify-center'
            }}
              previousControlIcon={<ChevronLeftIcon />}
              nextControlIcon={<ChevronRightIcon />}
            >
              {images.map((img, index) => (
                <Carousel.Slide key={index} className="tw-relative tw-flex tw-h-[358px] tw-justify-center">
                  <Image
                    src={img.gallery_image ?? img.src}
                    alt={`Product image ${index + 1}`}
                    style={{
                      objectFit: 'contain',
                      height: '100%',
                      width: '100%',
                    }}
                  />
                </Carousel.Slide>
              ))}
              </Carousel>
            ) : (
              /* Thumbnail Buttons for Desktop */
              <>
                <img src={selectedImage} alt="Selected Product" className="tw-w-full" />
                <div className="tw-flex tw-flex-row tw-justify-center tw-gap-8 tw-pb-48">
                  {images.map((media, index) => (
                    <button
                      key={index}
                      className={`tw-size-64 tw-rounded-4 tw-border-2 tw-border-solid tw-transition-colors tw-duration-500 ${
                        selectedImage === media.src ? 'tw-border-blue-500' : 'tw-border-transparent'
                      }`}
                      onClick={() => setSelectedImage(media.src)}
                    >
                      <img src={media.src} alt={`Thumbnail ${index}`} className="tw-w-full tw-rounded-4" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-px-20 tw-py-16 tablet:tw-w-1/2">
            {/* Close Button */}
             <XMarkIcon 
              onClick={onClose} 
              className="tw-absolute tw-right-16 tw-top-16 tw-size-20 tw-rounded-[5px] tw-bg-gray-50 tw-text-gray-500 hover:tw-cursor-pointer" 
              strokeWidth={2}
            />

            {/* Bread Crumbs */}
            <div className='cirk-label-small tw-flex tw-gap-4 tw-font-semibold'>
                <a href="/pages/bundle-builder#bb-starter-kits" className='tw-text-gray-400 hover:tw-underline'>Starter Kits</a>
                <>/</>
                <span className='tw-text-gray-500'>{product.title}</span>
            </div>

            {/* Product Title */}
            <div className='cirk-heading-h4 tw-pt-8 tw-font-bold tw-text-gray'>{product.title}</div>

            {/* Product Price */}
            <div className='tw-pt-4 tw-text-[20px] tw-leading-[28px] tw-tracking-[-0.4px] tw-text-gray-500 tablet:tw-text-[28px] tablet:tw-leading-[40px] tablet:tw-tracking-[-0.56px]'>{product.price}</div>

            {/* Add To Cart Button and Disclaimer (Mobile) */}
            {isMobile && 
                <>
                    <div className='tw-mt-20 tw-flex tw-flex-row tw-items-center tw-gap-16'>
                        { product.limit_per_customer > 1 && <ProductQuantity className='tw-w-1/3' />}
                        {<Button
                            size={BUTTON_SIZES.LARGE}
                            fullWidth={true}
                            onClick={() => handleAddToCart()}
                            dataUxTrackClick='Home Page - Starter Kit Modal - Add to Cart'
                            classNames={`tw-whitespace-nowrap ${product.limit_per_customer > 1 ? 'tw-w-3/5' : ''}`}
                            disabled={product.has_purchased_starter_kit || isProductInCart}
                        >
                            Add to Cart | {product.price}
                        </Button>}
                    </div>
                    <p className='cirk-label-small tw-pt-8 tw-text-right tw-text-gray-400'>Limit (1) one per account</p>
                </>
            }

            {/* Product Description */}
            <div className='cirk-paragraph-medium tw-pt-20 tw-text-gray' dangerouslySetInnerHTML={{ __html: product.description }}></div>

            {/* Nutrition Facts Link */}
            <div 
              onClick={handleDownloadAndOpenPDF}
              className={`cirk-label-large tw-pt-8 tw-font-bold tw-text-blue ${ isMobile && 'tw-pb-16'} tw-flex tw-cursor-pointer tw-gap-8`}
            >
              Download Nutrition Facts
              <ArrowDownTrayIcon 
                onClick={onClose} 
                className="tw-size-16 tw-text-blue-500 hover:tw-cursor-pointer" 
                strokeWidth={2}
              />
            </div>

            {/* Add To Cart Button and Disclaimer (Desktop) */}
            {!isMobile && 
                <>
                    <div className='tw-mt-20 tw-flex tw-flex-row tw-items-center tw-gap-16'>
                        { product.limit_per_customer > 1 && <ProductQuantity className='tw-w-1/3' />}
                        <Button
                            size={BUTTON_SIZES.LARGE}
                            fullWidth={true}
                            onClick={() => handleAddToCart()}
                            dataUxTrackClick='Home Page - Starter Kit Modal - Add to Cart'
                            classNames={`tw-whitespace-nowrap ${product.limit_per_customer > 1 ? 'tw-w-2/3' : ''}`}
                            disabled={product.has_purchased_starter_kit || isProductInCart}
                        >
                            Add to Cart | {product.price}
                        </Button>
                    </div>
                    <p className='cirk-label-small tw-pt-8 tw-text-right tw-text-gray-400'>Limit (1) one per account</p>
                </>
            }
          </div>
        </div>
      </div>
    </div>
  );
};